import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend, LineChart, Line
} from 'recharts';
import { 
  Leaf, TrendingUp, TrendingDown, Sun, Wind, Droplets,
  Target, FileText, Download, RefreshCw, CheckCircle,
  AlertTriangle, MapPin, Zap, Award, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPE_COLORS = {
  scope_1: '#EF4444',
  scope_2: '#3B82F6',
  scope_3: '#F59E0B'
};

const REC_TYPE_COLORS = {
  SOLAR: '#F59E0B',
  WIND: '#3B82F6',
  HYDRO: '#06B6D4',
  BIOMASS: '#22C55E',
  GEOTHERMAL: '#8B5CF6'
};

const STATUS_CONFIG = {
  ON_TRACK: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  AT_RISK: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  OFF_TRACK: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  ACHIEVED: { color: 'bg-blue-100 text-blue-700', icon: Award }
};

function TrendIndicator({ value, inverse = false }) {
  const isPositive = inverse ? value < 0 : value > 0;
  const isNegative = inverse ? value > 0 : value < 0;
  
  return (
    <div className={`flex items-center gap-1 text-sm font-medium ${
      isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-500'
    }`}>
      {value > 0 ? <ArrowUpRight className="w-4 h-4" /> : value < 0 ? <ArrowDownRight className="w-4 h-4" /> : null}
      {Math.abs(value)}%
    </div>
  );
}

function RECTypeIcon({ type }) {
  const icons = {
    SOLAR: Sun,
    WIND: Wind,
    HYDRO: Droplets,
    BIOMASS: Leaf,
    GEOTHERMAL: Zap
  };
  const Icon = icons[type] || Leaf;
  return <Icon className="w-4 h-4" style={{ color: REC_TYPE_COLORS[type] }} />;
}

export default function ESGDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRECs, setSelectedRECs] = useState([]);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // Fetch dashboard summary
  const { data: summaryData, isLoading } = useQuery({
    queryKey: ['esg-summary'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/esg/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch emissions data
  const { data: emissionsData } = useQuery({
    queryKey: ['esg-emissions'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/esg/emissions?period_months=12`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch REC inventory
  const { data: recsData } = useQuery({
    queryKey: ['esg-recs'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/esg/recs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch targets
  const { data: targetsData } = useQuery({
    queryKey: ['esg-targets'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/esg/targets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch reports
  const { data: reportsData } = useQuery({
    queryKey: ['esg-reports'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/esg/reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const summary = summaryData?.summary || {};
  const intensity = summaryData?.intensity_metrics || {};
  const emissions = emissionsData?.emissions || [];
  const recs = recsData?.recs || [];
  const recSummary = recsData?.summary || {};
  const targets = targetsData?.targets || [];
  const reports = reportsData?.reports || [];

  // Prepare chart data
  const emissionsTrendData = emissions.map(e => ({
    period: e.period,
    'Scope 1': e.scope_1,
    'Scope 2': e.scope_2,
    'Scope 3': e.scope_3,
    Total: e.total
  }));

  const scopeDistribution = [
    { name: 'Scope 1', value: summary.scope_breakdown?.scope_1 || 0, color: SCOPE_COLORS.scope_1 },
    { name: 'Scope 2', value: summary.scope_breakdown?.scope_2 || 0, color: SCOPE_COLORS.scope_2 },
    { name: 'Scope 3', value: summary.scope_breakdown?.scope_3 || 0, color: SCOPE_COLORS.scope_3 },
  ];

  const recTypeData = (recSummary.by_type || []).map(t => ({
    name: t.type,
    value: t.mwh,
    fill: REC_TYPE_COLORS[t.type] || '#888'
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="loading-state">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="esg-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-gradient-primary">ESG & Carbon Management</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Track emissions, manage RECs, and monitor sustainability targets
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowReportModal(true)}>
            <FileText className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
          <Button onClick={() => setShowRetireModal(true)} className="bg-green-600 hover:bg-green-700">
            <Leaf className="w-4 h-4 mr-2" />
            Retire RECs
          </Button>
        </div>
      </div>

      {/* Hero Summary */}
      <Card className="bg-gradient-to-br from-green-50 to-emerald-100 border-green-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-sm text-green-700 mb-1">Total Emissions</p>
              <p className="text-4xl font-bold text-green-800">{summary.total_emissions_tco2e || 0}</p>
              <p className="text-sm text-green-600">tCO₂e this month</p>
              <TrendIndicator value={summary.emissions_change_pct || 0} inverse />
            </div>
            <div className="text-center">
              <p className="text-sm text-green-700 mb-1">Renewable Coverage</p>
              <div className="relative w-24 h-24 mx-auto">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                  <circle 
                    cx="48" cy="48" r="40" fill="none" stroke="#22C55E" strokeWidth="8"
                    strokeDasharray={`${(summary.renewable_coverage_pct || 0) * 2.51} 251`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-green-800">{summary.renewable_coverage_pct || 0}%</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-green-700 mb-1">Active RECs</p>
              <p className="text-4xl font-bold text-green-800">{summary.active_recs_mwh?.toLocaleString() || 0}</p>
              <p className="text-sm text-green-600">MWh available</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-green-700 mb-1">Primary Target</p>
              <div className="flex items-center justify-center gap-2">
                <Badge className={STATUS_CONFIG[summary.primary_target?.status]?.color || 'bg-gray-100'}>
                  {summary.primary_target?.status?.replace('_', ' ') || 'N/A'}
                </Badge>
              </div>
              <p className="text-2xl font-bold text-green-800 mt-2">{summary.primary_target?.progress || 0}%</p>
              <p className="text-sm text-green-600">toward {summary.primary_target?.target_year}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="emissions">Emissions</TabsTrigger>
          <TabsTrigger value="recs">RECs & Renewables</TabsTrigger>
          <TabsTrigger value="targets">Targets</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Emissions Trend */}
            <Card data-testid="emissions-trend-chart">
              <CardHeader>
                <CardTitle className="text-lg">Emissions Trend</CardTitle>
                <CardDescription>Monthly emissions by scope (tCO₂e)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={emissionsTrendData}>
                    <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="Scope 1" stackId="1" fill={SCOPE_COLORS.scope_1} stroke={SCOPE_COLORS.scope_1} />
                    <Area type="monotone" dataKey="Scope 2" stackId="1" fill={SCOPE_COLORS.scope_2} stroke={SCOPE_COLORS.scope_2} />
                    <Area type="monotone" dataKey="Scope 3" stackId="1" fill={SCOPE_COLORS.scope_3} stroke={SCOPE_COLORS.scope_3} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ backgroundColor: SCOPE_COLORS.scope_1 }} /><span className="text-sm">Scope 1</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ backgroundColor: SCOPE_COLORS.scope_2 }} /><span className="text-sm">Scope 2</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ backgroundColor: SCOPE_COLORS.scope_3 }} /><span className="text-sm">Scope 3</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Scope Distribution */}
            <Card data-testid="scope-distribution">
              <CardHeader>
                <CardTitle className="text-lg">Scope Distribution</CardTitle>
                <CardDescription>Current month breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={scopeDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}t`}
                    >
                      {scopeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Intensity Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Carbon Intensity</p>
                    <p className="text-2xl font-bold">{intensity.kg_co2e_per_mwh || 0}</p>
                    <p className="text-xs text-muted-foreground">kgCO₂e/MWh</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Benchmark</p>
                    <p className="text-sm font-medium">{intensity.industry_benchmark_per_mwh || 420}</p>
                    <Badge className={intensity.kg_co2e_per_mwh < intensity.industry_benchmark_per_mwh ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {intensity.kg_co2e_per_mwh < intensity.industry_benchmark_per_mwh ? 'Below' : 'Above'} avg
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue Intensity</p>
                    <p className="text-2xl font-bold">{intensity.kg_co2e_per_revenue_1000 || 0}</p>
                    <p className="text-xs text-muted-foreground">kgCO₂e/$1K revenue</p>
                  </div>
                  <Zap className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">RECs Retired YTD</p>
                    <p className="text-2xl font-bold">{summary.retired_recs_mwh?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">MWh</p>
                  </div>
                  <Award className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Target Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Target Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {targets.map((target) => {
                  const StatusIcon = STATUS_CONFIG[target.status]?.icon || Target;
                  return (
                    <div key={target.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <StatusIcon className={`w-6 h-6 ${
                          target.status === 'ON_TRACK' ? 'text-green-500' :
                          target.status === 'AT_RISK' ? 'text-yellow-500' :
                          target.status === 'ACHIEVED' ? 'text-blue-500' : 'text-red-500'
                        }`} />
                        <div>
                          <p className="font-medium">{target.target_type.replace('_', ' ')}</p>
                          <p className="text-sm text-muted-foreground">
                            {target.science_based_target && <Badge variant="outline" className="text-xs mr-2">SBTi</Badge>}
                            Target: {target.target_value}% by {target.target_year}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32">
                          <Progress value={target.current_progress} className="h-2" />
                        </div>
                        <span className="font-bold w-16 text-right">{target.current_progress}%</span>
                        <Badge className={STATUS_CONFIG[target.status]?.color}>
                          {target.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emissions Tab */}
        <TabsContent value="emissions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Emissions History</CardTitle>
              <CardDescription>12-month trend with scope breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={emissionsTrendData}>
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Total" stroke="#1F2937" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Scope 1" stroke={SCOPE_COLORS.scope_1} strokeDasharray="5 5" dot={false} />
                  <Line type="monotone" dataKey="Scope 2" stroke={SCOPE_COLORS.scope_2} strokeDasharray="5 5" dot={false} />
                  <Line type="monotone" dataKey="Scope 3" stroke={SCOPE_COLORS.scope_3} strokeDasharray="5 5" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RECs Tab */}
        <TabsContent value="recs" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Active RECs</p>
                <p className="text-3xl font-bold text-green-600">{recSummary.total_active_mwh?.toLocaleString() || 0} MWh</p>
                <p className="text-sm text-muted-foreground">{recSummary.count_active || 0} certificates</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Retired YTD</p>
                <p className="text-3xl font-bold text-blue-600">{recSummary.total_retired_mwh?.toLocaleString() || 0} MWh</p>
                <p className="text-sm text-muted-foreground">{recSummary.count_retired || 0} certificates</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">REC Mix</p>
                <div className="flex items-center gap-2 mt-2">
                  {(recSummary.by_type || []).map(t => (
                    <div key={t.type} className="flex items-center gap-1">
                      <RECTypeIcon type={t.type} />
                      <span className="text-sm">{Math.round(t.mwh / (recSummary.total_active_mwh || 1) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">REC Inventory</CardTitle>
                  <CardDescription>Select RECs to retire</CardDescription>
                </div>
                {selectedRECs.length > 0 && (
                  <Button onClick={() => setShowRetireModal(true)} className="bg-green-600 hover:bg-green-700">
                    Retire {selectedRECs.length} Selected
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="py-3 px-2 w-8"></th>
                      <th className="text-left py-3 px-4">Certificate</th>
                      <th className="text-left py-3 px-4">Generator</th>
                      <th className="text-left py-3 px-4">Type</th>
                      <th className="text-left py-3 px-4">Location</th>
                      <th className="text-right py-3 px-4">MWh</th>
                      <th className="text-center py-3 px-4">Vintage</th>
                      <th className="text-center py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recs.map((rec) => (
                      <tr key={rec.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          {rec.status === 'ACTIVE' && (
                            <Checkbox
                              checked={selectedRECs.includes(rec.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedRECs([...selectedRECs, rec.id]);
                                } else {
                                  setSelectedRECs(selectedRECs.filter(id => id !== rec.id));
                                }
                              }}
                            />
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{rec.certificate_id}</td>
                        <td className="py-3 px-4">{rec.generator_name}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <RECTypeIcon type={rec.generator_type} />
                            {rec.generator_type}
                          </div>
                        </td>
                        <td className="py-3 px-4">{rec.generation_location}</td>
                        <td className="py-3 px-4 text-right font-medium">{rec.quantity_mwh.toLocaleString()}</td>
                        <td className="py-3 px-4 text-center">{rec.vintage_year}-{String(rec.vintage_month).padStart(2, '0')}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={rec.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {rec.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Targets Tab */}
        <TabsContent value="targets" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {targets.map((target) => {
              const StatusIcon = STATUS_CONFIG[target.status]?.icon || Target;
              return (
                <Card key={target.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        {target.target_type.replace('_', ' ')}
                      </CardTitle>
                      <Badge className={STATUS_CONFIG[target.status]?.color}>
                        {target.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    {target.science_based_target && (
                      <Badge variant="outline" className="w-fit">Science-Based Target (SBTi)</Badge>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between text-sm">
                        <span>Baseline: {target.baseline_year}</span>
                        <span>Target: {target.target_year}</span>
                      </div>
                      <Progress value={target.current_progress} className="h-4" />
                      <div className="flex justify-between">
                        <span className="text-2xl font-bold">{target.current_progress}%</span>
                        <span className="text-muted-foreground">of {target.target_value}%</span>
                      </div>
                      <div className="pt-4 border-t">
                        <p className="text-sm text-muted-foreground">
                          Gap to target: <span className="font-medium">{target.target_value - target.current_progress}%</span>
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Years remaining: <span className="font-medium">{target.target_year - new Date().getFullYear()}</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {reports.map((report) => (
              <Card key={report.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline">{report.framework}</Badge>
                    <Badge className={
                      report.status === 'SUBMITTED' ? 'bg-green-100 text-green-700' :
                      report.status === 'APPROVED' ? 'bg-blue-100 text-blue-700' :
                      report.status === 'DRAFT' ? 'bg-gray-100 text-gray-700' :
                      'bg-yellow-100 text-yellow-700'
                    }>
                      {report.status}
                    </Badge>
                  </div>
                  <h3 className="font-semibold">{report.framework} Report {report.reporting_year}</h3>
                  {report.score && (
                    <p className="text-sm text-muted-foreground mt-1">Score: <span className="font-medium">{report.score}</span></p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Generated: {new Date(report.generated_at).toLocaleDateString()}
                  </p>
                  {report.document_url && (
                    <Button variant="outline" size="sm" className="w-full mt-4">
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Retire RECs Modal */}
      <Dialog open={showRetireModal} onOpenChange={setShowRetireModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire RECs</DialogTitle>
            <DialogDescription>Retire selected renewable energy certificates for emissions offset</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">
              You are about to retire <span className="font-bold">{selectedRECs.length}</span> certificates.
            </p>
            <div>
              <label className="text-sm font-medium">Purpose</label>
              <Select defaultValue="scope2">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scope2">Scope 2 Emissions Offset</SelectItem>
                  <SelectItem value="customer">Customer Allocation</SelectItem>
                  <SelectItem value="voluntary">Voluntary Retirement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetireModal(false)}>Cancel</Button>
            <Button onClick={() => { setShowRetireModal(false); setSelectedRECs([]); }} className="bg-green-600 hover:bg-green-700">
              Confirm Retirement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate ESG Report</DialogTitle>
            <DialogDescription>Create a new sustainability report</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Framework</label>
              <Select defaultValue="CDP">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CDP">CDP Climate Change</SelectItem>
                  <SelectItem value="GRI">GRI Standards</SelectItem>
                  <SelectItem value="TCFD">TCFD</SelectItem>
                  <SelectItem value="CSRD">EU CSRD</SelectItem>
                  <SelectItem value="SASB">SASB</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Reporting Year</label>
              <Select defaultValue="2024">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
            <Button onClick={() => setShowReportModal(false)}>Generate Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
